// Wallet-Monitor API + Hyperliquid REST. V3-Routen:
// - GET    /eve/wallets       → WatchedWallet[]
// - POST   /eve/wallets       → WatchedWallet  (address + optional label/threshold)
// - PATCH  /eve/wallets/:id   → WatchedWallet  (label und/oder threshold)
// - DELETE /eve/wallets/:id
//
// Plus direkter Call zu Hyperliquid `info` für clearinghouseState.

import { apiFetch } from './api';
import { HL_FETCH_TIMEOUT_MS, HL_INFO_URL } from './wallet-monitor';
import type { ClearinghouseState, WatchedWallet } from '@/types/wallet-monitor';

export async function listWatchedWallets(accessToken: string): Promise<WatchedWallet[]> {
  return apiFetch<WatchedWallet[]>('/eve/wallets', { accessToken });
}

export interface AddWalletInput {
  address: string;
  label?: string | null;
  threshold_usd?: number;
}

export async function addWatchedWallet(
  input: AddWalletInput,
  accessToken: string,
): Promise<WatchedWallet> {
  return apiFetch<WatchedWallet>('/eve/wallets', {
    method: 'POST',
    accessToken,
    json: input,
  });
}

export async function deleteWatchedWallet(
  id: WatchedWallet['id'],
  accessToken: string,
): Promise<void> {
  await apiFetch(`/eve/wallets/${encodeURIComponent(String(id))}`, {
    method: 'DELETE',
    accessToken,
  });
}

export interface UpdateWalletInput {
  /** `null` löscht das Label (zeigt dann die Adresse). */
  label?: string | null;
  threshold_usd?: number;
}

export async function updateWatchedWallet(
  id: WatchedWallet['id'],
  patch: UpdateWalletInput,
  accessToken: string,
): Promise<WatchedWallet> {
  return apiFetch<WatchedWallet>(`/eve/wallets/${encodeURIComponent(String(id))}`, {
    method: 'PATCH',
    accessToken,
    json: patch,
  });
}

/**
 * Direkter Hyperliquid-Call (kein Worker dazwischen — die API ist öffentlich).
 * V1: 5s Timeout via AbortSignal.
 */
export async function fetchClearinghouseState(address: string): Promise<ClearinghouseState | null> {
  try {
    const res = await fetch(HL_INFO_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'clearinghouseState', user: address }),
      signal: AbortSignal.timeout(HL_FETCH_TIMEOUT_MS),
    });
    if (!res.ok) return null;
    return (await res.json()) as ClearinghouseState;
  } catch {
    return null;
  }
}
